import { useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { EPSelectPicker, EPInput } from "../../../common/controls";
import {
  createMeetingCredentials,
  credentialsByScheduleTypeId,
  getAllScheduleTypes,
  updateMeetingCredentials,
} from "../../../common/utils/api";
import { AxiosResponse } from "axios";
import {
  axiosGet,
  axiosGetParams,
  axiosPatch,
  axiosPost,
} from "../../../common/utils/helper";
import Loader from "../../Loader/loader";
import { toast } from "react-toastify";
import { CommonConstants } from "../../../common/utils/CommonConstants";
import CryptoJS from "crypto-js";
import { Modal, Whisper, Tooltip } from "rsuite";

interface Credential {
  ScheduleType: number;
  AccountId: string;
  ClientId: string;
  ClientSecret: string;
  TenantId: string;
  ServiceAccountJSON: string;
}

interface Props {
  open: boolean;
  action?: string;
  isValidate?: boolean;
  refresher?: boolean;
  setRefresher?: any;
  singleData?: any;
  setOpen: (open: boolean) => void;
}

export default function AddMeetingCredentials(props: Props) {
  const { open, setOpen, action, refresher, setRefresher, singleData } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [isValidate, setIsValidate] = useState<boolean>(false);
  const navigate = useNavigate();
  const [ScheduleType, setScheduleType] = useState([]);
  const [selectedMeetingType, setSelectedMeetingType] = useState<number>(0);
  const [credentialsData, setCredentialsData] = useState<Credential>({
    ScheduleType: singleData?.ScheduleType,
    AccountId: singleData?.AccountId,
    ClientId: singleData?.ClientId,
    ClientSecret: singleData?.ClientSecret,
    TenantId: singleData?.TenantId,
    ServiceAccountJSON: singleData?.ServiceAccountJSON,
  });
  const AES_SECRET_KEY = process.env.REACT_APP_AES_SECRET as string;

  const ScheduleTypeId = singleData?.ScheduleType;

  const handleOnChange = (value: string | number, name: string) => {
    setCredentialsData((data: any) => {
      return {
        ...data,
        [name]: value !== undefined ? value : data[name],
      };
    });
  };

  async function getMeetingCredentialsById(reqUrl: string) {
    const response = (await axiosGet(reqUrl, {})) as AxiosResponse;
    if (
      response &&
      response.status === CommonConstants.API_RESPONSE_SUCCESSFULL
    ) {
      const encryptedCredentialsData = response?.data?.data?.data;

      const decryptedCredentialsData = decrypt(
        encryptedCredentialsData?.Credentials
      );
      setCredentialsData((data: any) => {
        if (
          encryptedCredentialsData?.ScheduleType === CommonConstants.MS_TEAMS
        ) {
          return {
            ...data,
            ScheduleType: encryptedCredentialsData?.ScheduleType,
            AccountId: decryptedCredentialsData?.AccountId,
            TenantId: decryptedCredentialsData?.TenantId,
            ClientSecret: decryptedCredentialsData?.ClientSecret,
            ClientId: decryptedCredentialsData?.ClientId,
          };
        } else if (
          encryptedCredentialsData?.ScheduleType === CommonConstants.ZOOM
        ) {
          return {
            ...data,
            ScheduleType: encryptedCredentialsData?.ScheduleType,
            AccountId: decryptedCredentialsData?.AccountId,
            ClientSecret: decryptedCredentialsData?.ClientSecret,
            ClientId: decryptedCredentialsData?.ClientId,
          };
        } else if (
          encryptedCredentialsData?.ScheduleType === CommonConstants.GOOGLE_MEET
        ) {
          return {
            ...data,
            ScheduleType: encryptedCredentialsData?.ScheduleType,
            ServiceAccountJSON: encryptedCredentialsData?.ServiceAccountJSON,
          };
        }
      });
      setSelectedMeetingType(encryptedCredentialsData?.ScheduleType);
    }
  }

  async function postMeetingCredentials(reqUrl: string, reqBody: any) {
    setLoading(true);
    let response = (await axiosPost(reqUrl, { reqBody })) as AxiosResponse;
    if (
      response.status === CommonConstants.API_RESPONSE_SUCCESSFULL &&
      response.data?.status === "success"
    ) {
      setLoading(false);
      toast.success("Credentials verified and saved successfully", {
        position: toast.POSITION.TOP_CENTER,
      });
      navigate(`/meetingCredentials`);
      closeModal();
    } else if (
      response.status == CommonConstants.API_RESPONSE_SUCCESSFULL &&
      response.data?.status === "error"
    ) {
      setLoading(false);
      toast.warning(response?.data?.message, {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  }

  async function updateMeetingCredentialsById(reqUrl: string, reqBody: any) {
    setLoading(true);
    let response = (await axiosPatch(reqUrl, { reqBody })) as AxiosResponse;
    if (
      response.status === CommonConstants.API_RESPONSE_SUCCESSFULL &&
      response.data?.status === "success"
    ) {
      setLoading(false);
      toast.success("Credentials verified and updated successfully", {
        position: toast.POSITION.TOP_CENTER,
      });
      navigate(`/meetingCredentials`);
      closeModal();
    } else if (
      response.status == CommonConstants.API_RESPONSE_SUCCESSFULL &&
      response.data?.status === "error"
    ) {
      setLoading(false);
      toast.warning(response?.data?.message, {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  }

  const encrypt = (data: any) => {
    const ciphertext = CryptoJS.AES.encrypt(
      JSON.stringify(data),
      AES_SECRET_KEY
    ).toString();
    return ciphertext;
  };

  const decrypt = (data: any) => {
    const bytes = CryptoJS.AES.decrypt(data, AES_SECRET_KEY);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedData);
  };

  const createEncryptedPayload = () => {
    let payload: any = {};

    if (credentialsData?.ScheduleType === CommonConstants.MS_TEAMS) {
      payload = {
        ScheduleType: CommonConstants.MS_TEAMS,
        Credentials: encrypt({
          AccountId: credentialsData?.AccountId,
          TenantId: credentialsData?.TenantId,
          ClientSecret: credentialsData?.ClientSecret,
          ClientId: credentialsData?.ClientId,
        }),
      };
    } else if (credentialsData?.ScheduleType === CommonConstants.ZOOM) {
      payload = {
        ScheduleType: CommonConstants.ZOOM,
        Credentials: encrypt({
          AccountId: credentialsData?.AccountId,
          ClientSecret: credentialsData?.ClientSecret,
          ClientId: credentialsData?.ClientId,
        }),
      };
    } else if (credentialsData?.ScheduleType === CommonConstants.GOOGLE_MEET) {
      payload = {
        ScheduleType: CommonConstants.GOOGLE_MEET,
        Credentials: encrypt({
          ServiceAccountJSON: credentialsData?.ServiceAccountJSON,
        }),
      };
    }
    return payload;
  };

  function handleModalKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" && !loading) {
      handleSubmit();
    }
  }

  const handleSubmit = async () => {
    setIsValidate(true);
    let isEmpty = true;
    if (credentialsData?.ScheduleType === CommonConstants.ZOOM) {
      isEmpty =
        !credentialsData?.ClientId ||
        !credentialsData?.ClientSecret ||
        !credentialsData?.AccountId;
    } else if (credentialsData?.ScheduleType === CommonConstants.MS_TEAMS) {
      isEmpty =
        !credentialsData?.ClientId ||
        !credentialsData?.ClientSecret ||
        !credentialsData?.AccountId ||
        !credentialsData?.TenantId;
    } else if (credentialsData?.ScheduleType === CommonConstants.GOOGLE_MEET) {
      isEmpty = !credentialsData?.ServiceAccountJSON;
    }

    if (isEmpty) {
      const message = "Field cannot be empty.";
      toast.error(message, {
        position: toast.POSITION.TOP_CENTER,
      });
    } else {
      const payload = createEncryptedPayload();

      ScheduleTypeId
        ? await updateMeetingCredentialsById(
            `${updateMeetingCredentials}/${ScheduleTypeId}`,
            payload
          )
        : await postMeetingCredentials(createMeetingCredentials, payload);
      refresher ? setRefresher(false) : setRefresher(true);
    }
  };

  async function getInterviewType(reqUrl: string) {
    let response = (await axiosGetParams(reqUrl)) as AxiosResponse;
    if (
      response &&
      response?.status === CommonConstants.API_RESPONSE_SUCCESSFULL
    ) {
      if (response?.data?.data?.ScheduleTypeData?.length > 0) {
        const credentials = response?.data?.data?.ScheduleTypeData;
        setScheduleType(credentials);
      }
    }
  }

  const closeModal = () => {
    setOpen(false);
  };

  const handleMeetingTypeChange = (val: any) => {
    setSelectedMeetingType(val);
    handleOnChange(val, "ScheduleType");
    setCredentialsData((data) => ({
      ...data,
      TenantId: "",
      ClientId: "",
      ClientSecret: "",
      AccountId: "",
      ServiceAccountJSON: "",
    }));
  };

  useEffect(() => {
    getInterviewType(getAllScheduleTypes);
    ScheduleTypeId &&
      getMeetingCredentialsById(
        `${credentialsByScheduleTypeId}/${ScheduleTypeId}`
      );
  }, []);

  return loading ? (
    <Loader />
  ) : (
    <Modal
      open={open}
      onClose={closeModal}
      className="modal-sm"
      backdrop="static"
      keyboard={false}
      onKeyDown={handleModalKeyDown}
    >
      <Modal.Header closeButton={true}>
        {action === "Edit" ? "Edit Platform" : "Add Platform"}
      </Modal.Header>

      <Modal.Body>
        <Row>
          <Col xs lg="12" className="recruiter-label">
            Platform Type <span className="req">* </span>
          </Col>
          <Col xs lg="12" className="recruiter-label">
            <Whisper trigger="focus" speaker={<Tooltip>Required</Tooltip>}>
              <EPSelectPicker
                isValidate={isValidate}
                label="Schedule type"
                data={ScheduleType}
                value={credentialsData?.ScheduleType}
                onChange={handleMeetingTypeChange}
                disabled={ScheduleTypeId}
              />
            </Whisper>
          </Col>

          {credentialsData?.ScheduleType === CommonConstants?.MS_TEAMS && (
            <>
              <Col xs lg="12" className="recruiter-label">
                Tenant Id <span className="req">* </span>
              </Col>
              <Col xs lg="12" className="recruiter-label">
                <Whisper trigger="focus" speaker={<Tooltip>Required</Tooltip>}>
                  <EPInput
                    isValidate={isValidate}
                    minlength={3}
                    maxlength={255}
                    type="password"
                    placeholder="Tenant Id"
                    onChange={(val: string) => handleOnChange(val, "TenantId")}
                    value={credentialsData?.TenantId}
                  />
                </Whisper>
              </Col>
            </>
          )}

          {[CommonConstants?.ZOOM, CommonConstants?.MS_TEAMS].includes(
            credentialsData?.ScheduleType
          ) && (
            <>
              <Col xs lg="12" className="recruiter-label">
                Client Id <span className="req">* </span>
              </Col>
              <Col xs lg="12" className="recruiter-label">
                <Whisper trigger="focus" speaker={<Tooltip>Required</Tooltip>}>
                  <EPInput
                    isValidate={isValidate}
                    minlength={3}
                    maxlength={255}
                    type="password"
                    placeholder="Client Id"
                    onChange={(val: string) => handleOnChange(val, "ClientId")}
                    value={credentialsData?.ClientId}
                  />
                </Whisper>
              </Col>

              <Col xs lg="12" className="recruiter-label">
                Client Secret <span className="req">* </span>
              </Col>
              <Col xs lg="12" className="recruiter-label">
                <Whisper trigger="focus" speaker={<Tooltip>Required</Tooltip>}>
                  <EPInput
                    isValidate={isValidate}
                    minlength={3}
                    maxlength={255}
                    type="password"
                    placeholder="Client Secret"
                    onChange={(val: string) =>
                      handleOnChange(val, "ClientSecret")
                    }
                    value={credentialsData?.ClientSecret}
                  />
                </Whisper>
              </Col>

              <Col xs lg="12" className="recruiter-label">
                Account Id <span className="req">* </span>
              </Col>
              <Col xs lg="12" className="recruiter-label">
                <Whisper trigger="focus" speaker={<Tooltip>Required</Tooltip>}>
                  <EPInput
                    isValidate={isValidate}
                    minlength={3}
                    maxlength={255}
                    type="password"
                    placeholder="Account Id"
                    onChange={(val: string) => handleOnChange(val, "AccountId")}
                    value={credentialsData?.AccountId}
                  />
                </Whisper>
              </Col>
            </>
          )}

          {credentialsData?.ScheduleType === CommonConstants?.GOOGLE_MEET && (
            <>
              <Col xs lg="12" className="recruiter-label">
                Service Account JSON <span className="req">* </span>
              </Col>
              <Col xs lg="12" className="recruiter-label">
                <Whisper trigger="focus" speaker={<Tooltip>Required</Tooltip>}>
                  <EPInput
                    isValidate={isValidate}
                    textType="textarea"
                    rows={3}
                    minlength={3}
                    maxlength={10000}
                    // type="password"
                    placeholder="Service Account JSON"
                    onChange={(val: string) =>
                      handleOnChange(val, "ServiceAccountJSON")
                    }
                    value={credentialsData?.ServiceAccountJSON}
                  />
                </Whisper>
              </Col>
            </>
          )}
        </Row>
      </Modal.Body>
      <Modal.Footer className="pt-3">
        <Row>
          <Col xs lg>
            <Button
              type="button"
              disabled={!selectedMeetingType}
              onClick={() => handleSubmit()}
              className="btn btn-primary"
            >
              {action === "Edit" ? "Verify & Update" : "Verify & Save"}
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
}
